'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Edit3, Camera, MapPin, Calendar, Award, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface User {
  id: string;
  username: string;
  handle: string;
  bio: string;
  avatar: string;
  isVerified: boolean;
  placesVisited: number;
  reviewsCount: number;
  savedCount: number;
  joinedDate: string;
}

interface Props { user: User; }

export default function ProfilHeader({ user }: Props) {
  const supabase = createClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(user.username);
  const [editBio, setEditBio] = useState(user.bio);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(user.avatar);

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await supabase
      .from('user_profiles')
      .update({ full_name: editName, bio: editBio })
      .eq('id', user.id);

    if (error) {
      toast.error('Gagal menyimpan profil');
    } else {
      toast.success('Profil berhasil diperbarui! ✨');
      setIsEditing(false);
    }
    setIsSaving(false);
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${user.id}/avatar.${ext}`;

    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });

    if (uploadError) {
      toast.error('Gagal upload foto profil');
      setAvatarUploading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);

    await supabase.from('user_profiles').update({ avatar_url: publicUrl }).eq('id', user.id);
    setCurrentAvatar(publicUrl);
    toast.success('Foto profil diperbarui! 📸');
    setAvatarUploading(false);
  };

  return (
    <div className="mb-6">
      {/* Cover + Avatar */}
      <div className="relative mb-16">
        <div className="h-36 lg:h-48 rounded-2xl overflow-hidden bg-gradient-to-br from-primary to-accent">
          <div className="w-full h-full opacity-30 bg-gradient-to-br from-primary via-accent to-green-DEFAULT" />
        </div>

        {/* Avatar */}
        <div className="absolute -bottom-12 left-6 flex items-end gap-4">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-background shadow-card">
              <AppImage
                src={currentAvatar}
                alt={`Foto profil ${user.username}`}
                width={96}
                height={96}
                className="object-cover w-full h-full"
              />
            </div>
            <label className="absolute -bottom-2 -right-2 w-7 h-7 bg-accent rounded-full flex items-center justify-center shadow-card hover:bg-[#E06828] transition-colors cursor-pointer">
              {avatarUploading ? <Loader2 size={12} color="white" className="animate-spin" /> : <Camera size={13} color="white" />}
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} disabled={avatarUploading} />
            </label>
          </div>
        </div>

        {/* Edit button */}
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-card border border-border rounded-xl px-3 py-2 text-xs font-600 text-primary hover:border-accent hover:text-accent transition-colors shadow-card"
        >
          <Edit3 size={13} />
          Edit Profil
        </button>
      </div>

      {/* Profile Info */}
      {isEditing ? (
        <div className="bg-card border border-border rounded-2xl p-5 mb-5 animate-scale-in">
          <h3 className="text-sm font-700 text-primary mb-4">Edit Profil</h3>
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Nama</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Bio</label>
              <textarea
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                rows={3}
                className="input-field text-sm resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={isSaving} className="btn-primary flex-1 text-sm">
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : 'Simpan Perubahan'}
              </button>
              <button onClick={() => setIsEditing(false)} className="btn-secondary text-sm px-5">Batal</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-5">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h1 className="text-xl font-800 text-primary">{editName}</h1>
            {user.isVerified && (
              <span className="nongki-badge">
                <Award size={10} />
                Nongki Verified
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-2">{user.handle}</p>
          <p className="text-sm text-foreground leading-relaxed mb-3">{editBio}</p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><MapPin size={12} /> Malang, Jawa Timur</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> Bergabung {user.joinedDate}</span>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Tempat Dikunjungi', value: user.placesVisited, emoji: '📍' },
          { label: 'Ulasan', value: user.reviewsCount, emoji: '✍️' },
          { label: 'Tersimpan', value: user.savedCount, emoji: '🔖' },
        ].map((stat) => (
          <div key={`stat-${stat.label}`} className="bg-card border border-border rounded-xl p-4 text-center">
            <span className="text-xl">{stat.emoji}</span>
            <p className="text-xl font-800 text-primary mt-1 font-tabular">{stat.value}</p>
            <p className="text-xs text-muted-foreground font-500 leading-tight mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}